import React from 'react';
import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import { ShellLayout } from './layout/ShellLayout';
import { LoginPage } from './pages/LoginPage';
import { ForbiddenPage } from './pages/ForbiddenPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { allSuites } from '@suites/registry';
import { useAuth } from '@shared/auth/AuthContext';

import { Outlet } from 'react-router-dom';

const SuiteGuard: React.FC<{ requiredPerm?: string }> = ({ requiredPerm }) => {
  const { hasPermission } = useAuth();
  if (requiredPerm && !hasPermission(requiredPerm)) {
    return <ForbiddenPage />;
  }
  return <Outlet />;
};

const RootRedirect: React.FC = () => {
  const { hasPermission } = useAuth();

  // Find first suite the user has permission to view
  for (const s of allSuites) {
    if (!s.requiredPermission || hasPermission(s.requiredPermission)) {
      return <Navigate to={s.basePath} replace />;
    }
  }

  return <Navigate to="/core/contacts" replace />;
};

const MobileTechnicianPage = React.lazy(
  () => import('@suites/inventory/pages/MobileTechnicianPage')
);

const PublicBookingPage = React.lazy(
  () => import('../suites/core/modules/calendar/pages/PublicBookingPage')
);

const PublicSignerPage = React.lazy(
  () => import('../suites/core/modules/documents/pages/PublicSignerPage')
);

export function createAppRouter() {
  const suiteRouteObjects: RouteObject[] = allSuites.map((suite) => ({
    path: suite.basePath.replace(/^\//, ''),
    element: <SuiteGuard requiredPerm={suite.requiredPermission} />,
    children: suite.routes,
  }));

  return createBrowserRouter([
    {
      path: '/login',
      element: <LoginPage />,
    },
    {
      path: '/403',
      element: <ForbiddenPage />,
    },
    {
      path: '/m/technician',
      handle: { crumb: 'Mobile Technician' },
      element: (
        <React.Suspense fallback={<div className="p-4 text-center text-sm">Loading Technician App...</div>}>
          <MobileTechnicianPage />
        </React.Suspense>
      ),
    },
    {
      path: '/book/:tenantSlug/:username/:eventSlug',
      element: (
        <React.Suspense fallback={<div className="p-8 text-center text-sm">Loading Booking Portal...</div>}>
          <PublicBookingPage />
        </React.Suspense>
      ),
    },
    {
      path: '/sign/:token',
      element: (
        <React.Suspense fallback={<div className="p-8 text-center text-sm">Loading Signing Portal...</div>}>
          <PublicSignerPage />
        </React.Suspense>
      ),
    },
    {
      path: '/',
      element: <ShellLayout />,
      children: [
        {
          index: true,
          element: <RootRedirect />,
        },
        ...suiteRouteObjects,
        {
          path: '*',
          element: <NotFoundPage />,
        },
      ],
    },
  ]);
}
